import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Input } from '@/components/Input';
import { useRegister } from '@/lib/auth';
import { ApiError } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';

const schema = z.object({
  name: z.string().min(1, 'Enter your name'),
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type FormValues = z.infer<typeof schema>;

export function Register() {
  const navigate = useNavigate();
  const register = useRegister();
  const {
    register: field,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', password: '' },
  });

  const onSubmit = handleSubmit((values) => {
    register.mutate(values, {
      onSuccess: () => {
        trackEvent('sign_up', { method: 'email' });
        navigate('/dashboard', { replace: true });
      },
    });
  });

  const errorMessage =
    register.error instanceof ApiError
      ? register.error.message
      : register.error
        ? 'Could not create account. Try again.'
        : null;

  return (
    <div className="w-full max-w-md">
      <h1 className="font-display text-h1 font-bold">Create your account</h1>
      <p className="mt-3 text-body text-text-secondary">
        14-day free trial. No credit card required.
      </p>

      <form onSubmit={onSubmit} className="mt-10 flex flex-col gap-gap-element-lg">
        <Field id="name" label="Full name" error={errors.name?.message}>
          <Input
            id="name"
            autoComplete="name"
            invalid={!!errors.name}
            {...field('name')}
          />
        </Field>

        <Field id="email" label="Work email" error={errors.email?.message}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            invalid={!!errors.email}
            {...field('email')}
          />
        </Field>

        <Field
          id="password"
          label="Password"
          error={errors.password?.message}
          hint="At least 8 characters."
        >
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            invalid={!!errors.password}
            {...field('password')}
          />
        </Field>

        {errorMessage ? (
          <div className="rounded-md border border-danger/40 bg-danger/5 px-4 py-3 text-body-sm text-danger">
            {errorMessage}
          </div>
        ) : null}

        <Button type="submit" size="lg" disabled={register.isPending}>
          {register.isPending ? 'Creating account…' : 'Create account'}
        </Button>

        <p className="text-body-sm text-text-secondary">
          Already have an account?{' '}
          <Link to="/login" className="text-accent hover:underline">
            Sign in
          </Link>
        </p>
      </form>
    </div>
  );
}
